'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { newsletterSchema, type NewsletterInput } from '@/lib/validators/lead.schema';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface NewsletterFormProps {
  variant?: 'light' | 'dark';
}

export function NewsletterForm({ variant = 'light' }: NewsletterFormProps) {
  const t = useTranslations('public.footer');
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NewsletterInput>({
    resolver: zodResolver(newsletterSchema),
  });

  const onSubmit = async (data: NewsletterInput) => {
    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const result = await res.json();
        toast.error(result.error?.message ?? t('formError'));
        return;
      }

      setSubmitted(true);
    } catch {
      toast.error(t('networkError'));
    }
  };

  if (submitted) {
    return (
      <p className={`text-sm font-medium ${variant === 'dark' ? 'text-white' : 'text-eifo-primary'}`}>
        {t('subscribed')}
      </p>
    );
  }

  const isDark = variant === 'dark';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex gap-2">
      {/* Honeypot — hidden from humans */}
      <input
        {...register('honeypot')}
        aria-hidden="true"
        tabIndex={-1}
        autoComplete="off"
        className="absolute h-0 w-0 overflow-hidden opacity-0"
      />
      <div className="flex-1">
        <Input
          {...register('email')}
          type="email"
          placeholder={t('emailPlaceholder')}
          aria-label={t('emailAria')}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'newsletter-email-error' : undefined}
          className={isDark ? 'border-white/20 bg-white/10 text-white placeholder:text-white/60' : ''}
        />
        {errors.email && (
          <p id="newsletter-email-error" className="mt-1 text-xs text-red-400">
            {errors.email.message}
          </p>
        )}
      </div>
      <Button
        type="submit"
        disabled={isSubmitting}
        className="bg-eifo-orange text-white hover:bg-eifo-orange/90"
      >
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('subscribe')}
      </Button>
    </form>
  );
}
